import { useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Code,
  Container,
  CopyButton,
  Divider,
  Group,
  Paper,
  Progress,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconArrowRight, IconCheck, IconCode, IconCopy, IconPlayerPlay, IconRefresh } from "@tabler/icons-react";
import {
  openapi,
  type GetReportsStartupChecklistsStatisticsApiResponse as Statistics,
  type GetReportsStartupChecklistsStatisticsApiArg as Args,
} from "../../../../../openapi";
import { startupStatsExample, statisticsUrl } from "./startupStatsDemo";

const formatDate = (date: string | null) =>
  date ? new Date(date).toLocaleString("nb-NO", { dateStyle: "medium", timeStyle: "short" }) : "Ikke registrert";
const demoArgs: Args = { projectNo: 12345678, subTaskNo: "001" };
type Result = {
  source: "api" | "example";
  args: Args;
  url: string;
  data?: Statistics;
  error?: unknown;
  status: string;
  elapsed?: number;
  fetchedAt?: string;
};
const Copy = ({ value, label = "Kopier" }: { value: string; label?: string }) => (
  <CopyButton value={value}>
    {({ copied, copy }) => (
      <Button
        size="xs"
        variant="subtle"
        onClick={copy}
        leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
      >
        {copied ? "Kopiert" : label}
      </Button>
    )}
  </CopyButton>
);

export default function StartupStatsPage() {
  const [projectNo, setProjectNo] = useState("10216575");
  const [subTaskNo, setSubTaskNo] = useState("01");
  const [validation, setValidation] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [fetchStatistics, { isFetching }] = openapi.useLazyGetReportsStartupChecklistsStatisticsQuery();
  const args: Args = { projectNo: Number(projectNo), ...(subTaskNo ? { subTaskNo } : {}) };
  const valid = /^\d+$/.test(projectNo) && Number.isSafeInteger(args.projectNo) && args.projectNo > 0;
  const previewUrl = valid ? statisticsUrl(window.location.origin, args) : null;
  const shownUrl = result?.url ?? previewUrl;
  const rows = result?.data;
  const complete = rows?.filter((row) => row.completionPercent === 100).length ?? 0;
  const average = rows?.length
    ? Math.round(rows.reduce((sum, row) => sum + row.completionPercent, 0) / rows.length)
    : 0;
  const raw = result
    ? JSON.stringify(result.error ?? result.data, null, 2)
    : "// Kjør et API-kall eller velg eksempeldata.";
  const code = shownUrl
    ? `// Kjøres i integrasjonens backend med en gyldig integrasjonsnøkkel.\nconst response = await fetch(${JSON.stringify(
        shownUrl,
      )}, {\n  method: "GET",\n  headers: { Accept: "application/json", "x-api-key": integrationApiKey }\n});\nif (!response.ok) throw new Error(\`HTTP \${response.status}\`);\nconst checklists = await response.json();`
    : "// Angi et oppdragsnummer for å se koden.";
  const run = async () => {
    if (!valid) {
      setValidation("Angi et gyldig, positivt oppdragsnummer.");
      return;
    }
    setValidation(null);
    setResult(null);
    const started = performance.now();
    const requestUrl = statisticsUrl(window.location.origin, args);
    try {
      const data = await fetchStatistics(args, false).unwrap();
      if (!Array.isArray(data))
        throw {
          status: "Ugyldig svar",
          data: { message: "Forventet en liste fra API-et, men mottok ingen gyldig respons." },
        };
      setResult({
        source: "api",
        args,
        url: requestUrl,
        data,
        status: "200 OK",
        elapsed: Math.round(performance.now() - started),
        fetchedAt: new Date().toISOString(),
      });
    } catch (error) {
      setResult({
        source: "api",
        args,
        url: requestUrl,
        error: error?.data ?? error,
        status: String(error?.status ?? "Feil"),
        elapsed: Math.round(performance.now() - started),
      });
    }
  };
  const showExample = () => {
    setValidation(null);
    setResult({
      source: "example",
      args: demoArgs,
      url: statisticsUrl(window.location.origin, demoArgs),
      data: startupStatsExample,
      status: "Eksempel · ikke sendt",
    });
  };
  return (
    <Box style={{ height: "100vh", overflow: "auto", background: "#f3f6f9", color: "#183047" }}>
      <Box bg="#132e43" c="white" py={36}>
        <Container size={1280}>
          <Group gap="xs" mb="md">
            <Badge color="cyan" variant="light">
              MCApps × SharePoint
            </Badge>
            <Text size="xs" c="#b9cfde">
              INTEGRASJONSDEMO
            </Text>
          </Group>
          <Title order={1} size={34} fw={650}>
            Oppstart, samlet på ett sted.
          </Title>
          <Text mt="sm" c="#bdd0de" maw={700}>
            Se fremdriften i oppstartsjekklistene, prøv API-et og utforsk hvordan oversikten kan vises i SharePoint.
          </Text>
        </Container>
      </Box>
      <Container size={1280} py="xl">
        <Stack gap="lg">
          <Paper withBorder p="lg" radius="md">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void run();
              }}
            >
              <Group align="flex-start" justify="space-between" gap="lg">
                <Box>
                  <Text fw={600}>Velg oppdrag</Text>
                  <Text size="sm" c="dimmed">
                    Hent lagret statistikk fra MCApps.
                  </Text>
                </Box>
                <Group align="flex-start">
                  <TextInput
                    label="Oppdragsnummer"
                    placeholder="f.eks. 12345678"
                    inputMode="numeric"
                    value={projectNo}
                    disabled={isFetching}
                    onChange={(e) => setProjectNo(e.currentTarget.value)}
                    error={validation}
                  />
                  <TextInput
                    label="underoppdragsnr"
                    placeholder="f.eks. 001"
                    value={subTaskNo}
                    disabled={isFetching}
                    onChange={(e) => setSubTaskNo(e.currentTarget.value)}
                    description="Tomt = uten underoppdrag"
                    inputWrapperOrder={["label", "input", "description", "error"]}
                  />
                  <Button type="submit" mt={25} loading={isFetching} leftSection={<IconRefresh size={16} />}>
                    Hent statistikk
                  </Button>
                  <Button
                    mt={25}
                    variant="light"
                    disabled={isFetching}
                    onClick={showExample}
                    leftSection={<IconPlayerPlay size={16} />}
                  >
                    Vis eksempeldata
                  </Button>
                </Group>
              </Group>
            </form>
          </Paper>
          {result?.source === "example" && (
            <Alert color="blue" title="Presentasjonsmodus · fiktive data">
              Viser et eksempel med tre checklistemaler. Mal 902 og 903 er illustrasjoner av fremtidige maler. Ingen
              forespørsel er sendt til API-et.
            </Alert>
          )}
          {result?.error != null && (
            <Alert color="red" title={`API-kallet feilet · ${result.status}`}>
              {result.status === "401"
                ? "Integrasjonsnøkkelen mangler eller er ugyldig. Send en gyldig x-api-key."
                : result.status === "404"
                ? "Oppdraget finnes ikke. Kontroller oppdragsnummeret."
                : "Kunne ikke hente statistikk. Se API-svaret nedenfor."}
            </Alert>
          )}
          <Group justify="space-between" align="center">
            <Box>
              <Title order={2} size="h3">
                Oppstartsoversikt
              </Title>
              <Text size="sm" c="dimmed">
                {result
                  ? `Oppdrag ${result.args.projectNo} · ${
                      result.args.subTaskNo ? `Underoppdrag ${result.args.subTaskNo}` : "Uten underoppdrag"
                    }`
                  : "En mulig visning i SharePoint"}
              </Text>
            </Box>
            {result && (
              <Badge variant="light" color={result.source === "example" ? "blue" : result.error ? "red" : "teal"}>
                {result.source === "example" ? "Eksempeldata" : "API-data"}
              </Badge>
            )}
          </Group>
          {!rows && !isFetching && !result?.error && (
            <Paper withBorder radius="md" p={40} ta="center">
              <Title order={3} size="h4">
                Fra API til oppdragsoversikt
              </Title>
              <Text c="dimmed" mt="xs">
                Hent data fra et oppdrag, eller bruk eksempeldata for å demonstrere visningen.
              </Text>
              <Button mt="md" variant="subtle" onClick={showExample} rightSection={<IconArrowRight size={16} />}>
                Utforsk eksempelet
              </Button>
            </Paper>
          )}
          {isFetching && (
            <Paper p="xl" withBorder role="status">
              <Text>Henter statistikk…</Text>
            </Paper>
          )}
          {rows && (
            <>
              <SimpleGrid cols={{ base: 1, sm: 3 }}>
                {[
                  { label: "Sjekklister", value: rows.length, note: "På valgt oppdrag og underoppdrag" },
                  {
                    label: "Helt avklart",
                    value: `${complete} / ${rows.length}`,
                    note: "Sjekklister med 100 % fremdrift",
                  },
                  {
                    label: "Gjennomsnittlig fremdrift",
                    value: rows.length ? `${average} %` : "—",
                    note: "Uvektet snitt · hver sjekkliste teller likt",
                  },
                ].map((item) => (
                  <Paper key={item.label} withBorder p="lg" radius="md">
                    <Text size="sm" c="dimmed">
                      {item.label}
                    </Text>
                    <Text fz={32} fw={650} my={4}>
                      {item.value}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {item.note}
                    </Text>
                  </Paper>
                ))}
              </SimpleGrid>
              <Paper withBorder radius="md" p="lg">
                <Group justify="space-between" mb="lg">
                  <Title order={3} size="h4">
                    Fremdrift per sjekkliste
                  </Title>
                  <Text size="xs" c="dimmed">
                    0–100 %
                  </Text>
                </Group>
                {!rows.length ? (
                  <Text c="dimmed" py="xl" ta="center">
                    Ingen oppstartsjekklister matcher dette oppdraget og underoppdraget.
                  </Text>
                ) : (
                  <Stack gap="xl">
                    {rows.map((row) => (
                      <Box key={row.reportId}>
                        <Group justify="space-between" mb={8}>
                          <Group gap="xs">
                            <Text fw={600}>{row.name}</Text>
                            <Badge size="xs" variant="outline" color="gray">
                              Mal {row.templateId}
                            </Badge>
                          </Group>
                          <Text fw={700} c={row.completionPercent === 100 ? "#16836b" : "#4b83b0"}>
                            {row.completionPercent} %
                          </Text>
                        </Group>
                        <Progress
                          value={row.completionPercent}
                          color={row.completionPercent === 100 ? "#16836b" : "#4b83b0"}
                          size={12}
                          radius="xl"
                          aria-label={`${row.name}: ${row.completionPercent} prosent avklart`}
                        />
                        <Group justify="space-between" mt={8} gap="xs">
                          <Text size="xs" c="dimmed">
                            Sist oppdatert {formatDate(row.updatedAt)} · {row.lastUpdatedBy?.name ?? "Ukjent bruker"}
                          </Text>
                          <Badge variant="light" size="sm" color={row.status === "COMPLETED" ? "teal" : "gray"}>
                            {row.status === "COMPLETED" ? "Avsluttet" : row.status === "ACTIVE" ? "Aktiv" : "Slettet"}
                          </Badge>
                        </Group>
                        <Text size="xs" c="dimmed" mt={4}>
                          Opprettet {formatDate(row.createdAt)} av {row.createdBy?.name ?? "ukjent bruker"}
                        </Text>
                      </Box>
                    ))}
                  </Stack>
                )}
                <Divider my="lg" />
                <Text size="xs" c="dimmed">
                  Fremdrift beregnes etter reglene i hver mal. For 901 teller Ja, Nei og Ikke relevant som avklart.
                  Uavklart teller ikke. 100 % betyr ikke at alle krav er oppfylt. Rapportstatus er et eget felt.
                </Text>
              </Paper>
            </>
          )}
          <Paper withBorder p="lg" radius="md">
            <Group justify="space-between" mb="md">
              <Group gap="xs">
                <IconCode size={20} />
                <Title order={2} size="h4">
                  For utvikleren
                </Title>
              </Group>
              {result && (
                <Group gap="xs">
                  <Badge color={result.error ? "red" : "gray"} variant="light">
                    {result.status}
                  </Badge>
                  {result.elapsed != null && (
                    <Text size="xs" c="dimmed">
                      {result.elapsed} ms
                    </Text>
                  )}
                </Group>
              )}
            </Group>
            <Text size="sm" c="dimmed" mb="xs">
              {result ? "Anropet som hører til resultatet over" : "Forhåndsvisning av API-anropet"}
            </Text>
            <Group wrap="nowrap" align="flex-start">
              <Badge color="teal" mt={6}>
                GET
              </Badge>
              <Code block style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}>
                {shownUrl ?? "Angi oppdragsnummer, eller velg eksempeldata."}
              </Code>
              {shownUrl && <Copy value={shownUrl} label="Kopier URL" />}
            </Group>
            {result && previewUrl && previewUrl !== result.url && (
              <Text size="xs" c="orange" mt="xs">
                Feltene er endret. Trykk Hent statistikk for å hente et nytt resultat.
              </Text>
            )}
            <Tabs defaultValue="request" mt="lg">
              <Tabs.List>
                <Tabs.Tab value="request">Anrop</Tabs.Tab>
                <Tabs.Tab value="response">JSON-resultat</Tabs.Tab>
                <Tabs.Tab value="code">JavaScript</Tabs.Tab>
                <Tabs.Tab value="contract">Datakontrakt</Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="request" pt="md">
                <Stack gap="sm">
                  <Text size="sm">
                    <b>Body:</b> Ingen. Dette er GET; parametrene sendes i URL-en.
                  </Text>
                  <Text size="sm">Query-parametere:</Text>
                  <Code block>
                    {JSON.stringify(
                      result?.args ??
                        (valid ? args : { projectNo: "<oppdragsnummer>", subTaskNo: "<valgfritt underoppdrag>" }),
                      null,
                      2,
                    )}
                  </Code>
                  <Text size="sm">
                    <b>Autentisering:</b> Endpointen bruker integrationProcedure med x-api-key. Ingen innlogget bruker
                    eller prosjektmedlemskap kreves. I utviklingsmiljø hopper eksisterende middleware over
                    nøkkelkontrollen. Demosiden bruker en eventuell API-nøkkel som allerede er konfigurert i MCApps.
                  </Text>
                  <Text size="sm" c="dimmed">
                    La SharePoint-integrasjonens backend legge til integrasjonsnøkkelen. Ikke bygg den inn i
                    klientkode eller URL-er. Kodeeksempelet forutsetter at integrationApiKey er satt i backend.
                  </Text>
                </Stack>
              </Tabs.Panel>
              <Tabs.Panel value="response" pt="md">
                <Group justify="space-between" mb="xs">
                  <Text size="sm">
                    {result?.source === "example" ? "Fiktivt eksempel · samme responsformat" : "API-svar (JSON)"}
                  </Text>
                  <Copy value={raw} label="Kopier JSON" />
                </Group>
                {result?.fetchedAt && (
                  <Text size="xs" c="dimmed" mb="xs">
                    Hentet {formatDate(result.fetchedAt)}
                  </Text>
                )}
                <Code block style={{ maxHeight: 420, overflow: "auto" }}>
                  {raw}
                </Code>
              </Tabs.Panel>
              <Tabs.Panel value="code" pt="md">
                <Group justify="space-between" mb="xs">
                  <Text size="sm">Fetch · OpenAPI-adapteren til tRPC</Text>
                  <Copy value={code} label="Kopier kode" />
                </Group>
                <Code block>{code}</Code>
                <Text size="xs" c="dimmed" mt="sm">
                  tRPC-prosedyre: trpcGetStartupChecklistStatistics
                </Text>
              </Tabs.Panel>
              <Tabs.Panel value="contract" pt="md">
                <Stack gap="xs">
                  <Text size="sm">
                    <b>Respons:</b> en liste med reportId, name, templateId, status, completionPercent, createdAt,
                    createdBy, updatedAt og lastUpdatedBy.
                  </Text>
                  <Text size="sm">
                    Brukere returneres som {"{ _id, name }"}. Datoer er ISO 8601 i UTC; visningen over bruker
                    nettleserens tidssone. Manglende bruker- og tidsopplysninger er null.
                  </Text>
                  <Text size="sm">
                    subTaskNo matches eksakt mot doksam.subTaskNo. Ledende nuller bevares. Utelatt parameter betyr kun
                    sjekklister uten underoppdrag. Slettede rapporter utelates; ingen treff gir [].
                  </Text>
                  <Text size="sm">
                    completionPercent er et heltall fra 0 til 100 per sjekkliste. Gjennomsnittet i demonstrasjonen
                    beregnes i frontend og er ikke et eget API-felt.
                  </Text>
                  <Text size="sm">
                    401: manglende eller ugyldig integrasjonsnøkkel. 404: oppdraget finnes ikke. Statistikken gjelder
                    senest lagrede data.
                  </Text>
                </Stack>
              </Tabs.Panel>
            </Tabs>
          </Paper>
          <Text size="xs" c="dimmed" ta="center">
            MCApps · Oppstartsstatistikk · Integrasjonsdemo for SharePoint
          </Text>
        </Stack>
      </Container>
    </Box>
  );
}
